import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, Button, Chip, CircularProgress } from "@mui/material";
import { LocalShipping } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import deliveryReceiptService from "../../services/deliveryReceiptService";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const RiderAvailableDeliveries = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claimingId, setClaimingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await deliveryReceiptService.getAvailable();
            setReceipts(data.data.receipts || []);
        } catch {
            enqueueSnackbar("Failed to load available deliveries", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleClaim = async (id) => {
        setClaimingId(id);
        try {
            await deliveryReceiptService.claim(id);
            enqueueSnackbar("Delivery claimed", { variant: "success" });
            navigate("/rider/mine");
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to claim delivery", { variant: "error" });
            load();
        } finally {
            setClaimingId(null);
        }
    };

    if (loading) return <SkeletonLoader rows={4} />;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" fontWeight={700}>
                Available Deliveries ({receipts.length})
            </Typography>

            {receipts.length === 0 ? (
                <EmptyState icon="📦" title="No deliveries available" description="Check back soon for new deliveries." />
            ) : (
                receipts.map((r) => (
                    <Card key={r._id}>
                        <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                <Box>
                                    <Typography fontWeight={700}>
                                        {r.customer?.firstName} {r.customer?.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {r.receiptNumber}
                                    </Typography>
                                </Box>
                                <Chip label={`${r.shipments?.length || 0} pkg`} size="small" />
                            </Box>
                            <Typography variant="body2" color="text.secondary" mb={1.5}>
                                {r.deliveryAddress || "No address on file"}
                            </Typography>
                            {r.shipments?.map((s) => (
                                <Typography key={s._id} variant="caption" display="block" color="text.secondary">
                                    • {s.trackingNumber} — {s.packageDescription || "Package"}
                                </Typography>
                            ))}
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<LocalShipping />}
                                sx={{ mt: 2 }}
                                onClick={() => handleClaim(r._id)}
                                disabled={claimingId === r._id}
                            >
                                {claimingId === r._id ? <CircularProgress size={18} color="inherit" /> : "Claim Delivery"}
                            </Button>
                        </CardContent>
                    </Card>
                ))
            )}
        </Box>
    );
};

export default RiderAvailableDeliveries;